import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCheck, faCopy, faFingerprint } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { UUID_ABOUT } from '../content/toolGuides';
import { UUID_ROUTE } from '../content/tools';
import {
  generateUuids,
  MAX_UUID_COUNT,
  UuidVersion,
  UUID_VERSIONS,
} from '../utils/uuid';
import './UuidGenerator.css';

const UuidGenerator: React.FC = () => {
  const [version, setVersion] = useState<UuidVersion>('v4');
  const [count, setCount] = useState(5);
  const [uppercase, setUppercase] = useState(false);
  const [hyphens, setHyphens] = useState(true);
  const [braces, setBraces] = useState(false);
  const [uuids, setUuids] = useState<string[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(() => {
    try {
      setUuids(generateUuids(count, version, { uppercase, hyphens, braces }));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
      setUuids([]);
    }
  }, [count, version, uppercase, hyphens, braces]);

  // A generator that opens empty has nothing to show, so mint the first batch
  // on mount and whenever the options change.
  useEffect(() => {
    generate();
  }, [generate]);

  const copy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const selected = UUID_VERSIONS.find((entry) => entry.value === version);

  return (
    <>
      <RouteSeo route={UUID_ROUTE} />
      <ToolLayout
        title="UUID Generator"
        description="Generate random v4 or time-ordered v7 UUIDs in bulk"
        icon={faFingerprint}
        error={error}
        onDismissError={() => setError(null)}
        inputContent={
          <Form className="uuid-options">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="uuid-version">
                <strong>Version</strong>
              </Form.Label>
              <Form.Select
                id="uuid-version"
                value={version}
                onChange={(event) => setVersion(event.target.value as UuidVersion)}
              >
                {UUID_VERSIONS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Form.Select>
              {selected && <Form.Text className="text-muted">{selected.hint}</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="uuid-count">
                <strong>How many</strong>
              </Form.Label>
              <Form.Control
                id="uuid-count"
                type="number"
                min={1}
                max={MAX_UUID_COUNT}
                value={count}
                onChange={(event) => setCount(Number(event.target.value))}
              />
              <Form.Text className="text-muted">1 to {MAX_UUID_COUNT} at a time.</Form.Text>
            </Form.Group>

            <fieldset className="uuid-format">
              <legend className="uuid-format__legend">Format</legend>
              <Form.Check
                type="checkbox"
                id="uuid-uppercase"
                label="Uppercase"
                checked={uppercase}
                onChange={(event) => setUppercase(event.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="uuid-hyphens"
                label="Hyphens"
                checked={hyphens}
                onChange={(event) => setHyphens(event.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="uuid-braces"
                label="Braces {…}"
                checked={braces}
                onChange={(event) => setBraces(event.target.checked)}
              />
            </fieldset>
          </Form>
        }
        actions={
          <>
            <Button variant="primary" onClick={generate}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              Generate
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => copy(uuids.join('\n'), 'all')}
              disabled={uuids.length === 0}
            >
              <FontAwesomeIcon icon={copiedField === 'all' ? faCheck : faCopy} className="me-2" />
              {copiedField === 'all' ? 'Copied!' : 'Copy All'}
            </Button>
          </>
        }
        outputContent={
          uuids.length > 0 ? (
            <>
              <h3 className="uuid-list__heading">
                {uuids.length} {version.toUpperCase()} {uuids.length === 1 ? 'UUID' : 'UUIDs'}
              </h3>
              <ol className="uuid-list">
                {uuids.map((uuid, index) => (
                  <li className="uuid-list__item" key={`${uuid}-${index}`}>
                    <code className="uuid-list__value">{uuid}</code>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => copy(uuid, uuid)}
                      aria-label={`Copy ${uuid}`}
                    >
                      <FontAwesomeIcon icon={copiedField === uuid ? faCheck : faCopy} />
                    </Button>
                  </li>
                ))}
              </ol>
            </>
          ) : (
            <p className="text-muted mb-0">Press Generate to mint a batch of UUIDs.</p>
          )
        }
        aboutTitle="About UUIDs"
        about={UUID_ABOUT}
      >
        <ToolContent route={UUID_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default UuidGenerator;
